import { CircularProgress } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import globalMessages from '../../../../../../config/globalMessages';
import { formatAxisTick, formatStatValue, toDisplaySpeed } from './statisticsOverlayHelpers';

export default function metricCalculationDescriptions({ dataset, summary, days, effectiveMinutes, netMinutes, rangeMinutes, rangeLabel, unassignedStopMinutes }) {
  const withDefaultStops = (description) =>
    dataset?.allowStopAnnotations ? (
      <>
        {description}
        {'\n\n'}
        <FormattedMessage
          {...globalMessages.statisticsDefaultLedinekExplanation}
          values={{
            minutes: unassignedStopMinutes === undefined ? <CircularProgress size={12} color="inherit" aria-label="Loading default Ledinek minutes" /> : formatStatValue(unassignedStopMinutes, 0)
          }}
        />
      </>
    ) : (
      description
    );
  const outputUnit = dataset?.speedUnit ? 'm' : <FormattedMessage {...globalMessages.statisticsOutputUnits} />;
  const speedUnit = dataset?.speedUnit ?? <FormattedMessage {...globalMessages.statisticsSpeedUnits} />;
  const factor = dataset?.speedUnit === 'm/h' ? 60 : 1;
  const speed = (field, result) => {
    const values = summary?.speedCalculations?.[field];
    if (!values?.divisor || result == null) return <FormattedMessage {...globalMessages.statisticsSpeedUnavailable} />;
    return values.basis === 'minutes' ? (
      <FormattedMessage
        {...globalMessages.statisticsSpeedMinutesCalculation}
        values={{
          kind: field === 'actualSpeed' ? 'actual' : 'expected',
          conversion: factor === 60 ? ' x 60' : '',
          total: formatStatValue(values.total),
          outputUnit,
          minutes: formatStatValue(values.divisor),
          speed: formatStatValue(toDisplaySpeed(result, dataset?.speedUnit)),
          speedUnit
        }}
      />
    ) : (
      <FormattedMessage
        {...globalMessages.statisticsSpeedReadingsCalculation}
        values={{
          total: formatStatValue(values.total * factor),
          readings: values.divisor,
          speed: formatStatValue(toDisplaySpeed(result, dataset?.speedUnit)),
          speedUnit
        }}
      />
    );
  };
  const efficiency = (values) =>
    values?.expectedTotal > 0 ? (
      <FormattedMessage
        {...globalMessages.statisticsEfficiencyCalculation}
        values={{
          actual: formatStatValue(values.actualTotal),
          expected: formatStatValue(values.expectedTotal),
          efficiency: formatStatValue(values.efficiency),
          outputUnit
        }}
      />
    ) : (
      <FormattedMessage {...globalMessages.statisticsEfficiencyUnavailable} />
    );
  const formula = dataset?.expectedSpeedFormula;
  const target = formula ? (
    <FormattedMessage
      {...globalMessages.statisticsExpectedSpeedCalculation}
      values={{
        referenceLength: formula.referenceLength,
        minWidth: formula.minArea / 50,
        maxWidth: formula.maxArea / 50,
        minAreaSpeed: formula.minAreaSpeed,
        maxAreaSpeed: formula.maxAreaSpeed,
        lengthPercent: formatStatValue(Math.min(2400 / formula.referenceLength, 1) * 100, 0),
        minTarget: formatStatValue(dataset.calculateExpectedSpeed(formula.minArea, 2400) * 60),
        maxTarget: formatStatValue(dataset.calculateExpectedSpeed(formula.maxArea, 2400) * 60)
      }}
    />
  ) : null;
  const day = days?.find((entry) => entry.productionStart);
  const effective = (
    <FormattedMessage
      {...globalMessages.statisticsEffectiveTimeCalculation}
      values={{
        example: day ? (
          <FormattedMessage
            {...globalMessages.statisticsEffectiveTimeExample}
            values={{
              start: formatAxisTick(day.productionStart),
              end: formatAxisTick(day.productionEnd),
              minutes: formatStatValue(day.effectiveMinutes)
            }}
          />
        ) : (
          ''
        )
      }}
    />
  );
  const net = (
    <FormattedMessage
      {...globalMessages.statisticsNetTimeCalculation}
      values={{
        effective: formatStatValue(effectiveMinutes),
        excluded: formatStatValue(effectiveMinutes == null || netMinutes == null ? null : effectiveMinutes - netMinutes),
        net: formatStatValue(netMinutes)
      }}
    />
  );
  const bestHour = days?.find((entry) => entry.bestHour)?.bestHour;
  return {
    actualSpeedAverage: withDefaultStops(formula ? <FormattedMessage {...globalMessages.statisticsActualCapacityCalculation} /> : speed('actualSpeed', summary?.actualSpeedAverage)),
    expectedSpeedAverage: target ?? speed('expectedSpeed', summary?.expectedSpeedAverage),
    contractSpeedAverage: target ?? speed('contractSpeed', summary?.contractSpeedAverage),
    efficiency: withDefaultStops(formula ? <FormattedMessage {...globalMessages.statisticsCapacityEfficiencyCalculation} /> : efficiency(summary?.efficiencyCalculation)),
    contractEfficiency: efficiency(summary?.contractEfficiencyCalculation),
    averageLength: <FormattedMessage {...globalMessages.statisticsXcutDescription} />,
    timeframe: <FormattedMessage {...globalMessages.statisticsTimeframeCalculation} values={{ range: rangeLabel, minutes: formatStatValue(rangeMinutes) }} />,
    runningMetres: <FormattedMessage {...globalMessages.statisticsRunningMetersDescription} />,
    productionStart: <FormattedMessage {...globalMessages.statisticsProductionStartDescription} />,
    productionEnd: <FormattedMessage {...globalMessages.statisticsProductionEndDescription} />,
    effectiveTime: effective,
    netRunningTime: withDefaultStops(net),
    productionTime: withDefaultStops(<FormattedMessage {...globalMessages.statisticsProductionTimeCalculation} />),
    bestHour: (
      <FormattedMessage
        {...globalMessages.statisticsBestHourCalculation}
        values={{
          example: bestHour ? (
            <FormattedMessage
              {...globalMessages.statisticsBestHourExample}
              values={{
                start: formatAxisTick(bestHour.start),
                end: formatAxisTick(bestHour.end),
                meters: formatStatValue(bestHour.metres)
              }}
            />
          ) : (
            ''
          )
        }}
      />
    )
  };
}
